// Deterministic per-book presentation so the shelf looks hand-filled
// (mixed heights, widths, and colors) but never shuffles between renders.
// Every book on a year shelf stands upright on its spine; only the
// "reading now" shelf shows covers, and it asks for that mode directly.

const hashString = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

export const PLACARD_WIDTH = 168;
export const PLACARD_GAP = 16;

// Row geometry, shared with .shelf-row in bookshelf.css through inline custom
// properties so the fill maths and the layout can't drift apart.
export const SHELF_GAP = 3;
export const SHELF_PAD = 12;

// A shelf is filled by at most this many books; any more and the rest run off
// the end for you to scroll to.
export const FILL_LIMIT = 20;

// How far a spine may be stretched or squeezed from its natural thickness.
// The upper bound is set so a year of ten books or more still fills a full-width
// shelf, while a year that has only just begun stays a short row of books rather
// than three doors standing on a rule. The lower bound keeps spines readable on
// a phone, where twenty books simply cannot fit and the shelf scrolls instead.
const MIN_SCALE = 0.75;
const MAX_SCALE = 2.8;

// Spine colors live in bookshelf.css as .spine-c0 … .spine-c7
export const SPINE_PALETTE_SIZE = 8;

const SPINE_FACES = ['sans', 'serif', 'caps'];

// Title as printed on the spine: drop a subtitle after a colon.
export function spineTitle(title) {
  const idx = title.indexOf(':');
  return idx > 0 ? title.slice(0, idx).trim() : title;
}

export function bookMetrics(book) {
  const h = hashString(`${book.title}|${book.author}`);
  const shortTitle = spineTitle(book.title);

  const height = 172 + (h % 38); // 172–209px
  const widthFromTitle = Math.min(shortTitle.length, 30) / 3; // 0–10px
  const spineWidth = Math.round(36 + ((h >> 8) % 9) + widthFromTitle); // ~36–54px
  const coverWidth = Math.round(height * 0.63);
  const face = SPINE_FACES[(h >> 16) % SPINE_FACES.length];
  const palette = (h >> 4) % SPINE_PALETTE_SIZE;

  return { height, spineWidth, coverWidth, face, palette, shortTitle };
}

export function openSlotWidth(coverWidth) {
  return coverWidth + PLACARD_GAP + PLACARD_WIDTH;
}

const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);

/**
 * Thicken (or thin) every spine by one shared factor so the first FILL_LIMIT
 * books span the shelf exactly, leaving no bare rule at the right-hand end.
 * Books past that limit keep the same thickness and overflow into the scroll.
 *
 * One factor for the whole shelf rather than a width each keeps the hand-filled
 * variety: a slim book stays slimmer than its neighbors, just less so.
 *
 * @param {number[]} naturalWidths spine widths from bookMetrics, in order
 * @param {number} shelfWidth the scroller's visible width in px
 * @returns {number[]} integer widths, one per book
 */
export function fitSpineWidths(naturalWidths, shelfWidth) {
  const n = naturalWidths.length;
  if (n === 0) return [];

  const fill = Math.min(n, FILL_LIMIT);
  const available = shelfWidth - 2 * SHELF_PAD - SHELF_GAP * (fill - 1);
  const natural = naturalWidths.slice(0, fill).reduce((sum, w) => sum + w, 0);
  // Before the shelf has been measured there is nothing to fill.
  if (available <= 0 || natural <= 0) return naturalWidths;

  const scale = clamp(available / natural, MIN_SCALE, MAX_SCALE);

  // Round by carrying the running error forward, so the filled books add up to
  // `available` to the pixel instead of leaving a sliver of shelf showing.
  let exact = 0;
  let placed = 0;
  return naturalWidths.map((w) => {
    exact += w * scale;
    const next = Math.round(exact);
    const width = next - placed;
    placed = next;
    return width;
  });
}
